import { useCallback, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import studioApiClient, {
  AnalyticsEvent,
  postAnonymousAnalytics,
} from '../api/studio';
import useAnalyticsDeviceType from './useAnalyticsDeviceType';
import { AppContext } from '../contexts/AppContext';

export { DeviceType } from './useAnalyticsDeviceType';

export default function useAnalytics() {
  const deviceType = useAnalyticsDeviceType();
  const { isAdmin, isViewer } = useContext(AppContext);

  const { mutate: trackEventBase } = useMutation({
    mutationFn: (event: AnalyticsEvent) => studioApiClient.postAnalytics(event),
    onError: console.error,
  });

  const { mutate: trackAnonymousEventBase } = useMutation({
    mutationFn: (event: AnalyticsEvent) => postAnonymousAnalytics(event),
    onError: console.error,
  });

  // Stable identities — trackEvent is used in effect dependency arrays
  // (e.g. ResultStep's palette generation), so it must not change per render
  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      if (isAdmin || isViewer) return;
      trackEventBase({ ...event, deviceType });
    },
    [trackEventBase, deviceType, isAdmin, isViewer],
  );

  const trackAnonymousEvent = useCallback(
    (event: AnalyticsEvent) => {
      if (isAdmin || isViewer) return;
      trackAnonymousEventBase({ ...event, deviceType });
    },
    [trackAnonymousEventBase, deviceType, isAdmin, isViewer],
  );

  return { trackEvent, trackAnonymousEvent };
}
